import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { Col } from 'react-styled-flexboxgrid'
import styled from 'styled-components'
import media from 'css-in-js-media'
import {
    motion,
    useSpring,
    useTransform,
    useViewportScroll,
} from 'framer-motion'
import { useCurrentHeight } from './windowSize'

const StyledCol = styled(Col)<{ $bgImage: string }>`
    position: relative;
    height: 100vh;
    min-height: 900px;
    padding: 0;
    overflow: hidden;

    ${media('<desktop')} {
        min-height: 0;
        height: 0;
        padding-bottom: 125%;
    }
`

const StyledFrame = styled(Col)<{ $bgImage: string }>`
    background-image: ${(props) => `url(${props.$bgImage})`};
    background-position: center center;
    background-size: cover;
    position: relative;
    height: 100vh;
    min-height: 900px;
    padding: 0;
    overflow: hidden;
    width: calc(100% + 100px);
    margin-left: -100px;

    ${media('<desktop')} {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        min-height: 100%;
    }
`

interface Props {
    mobileWidth: number
    width: number
    bgImage: string
}

const BlockImage = ({ width, mobileWidth, bgImage, ...rest }: Props) => {
    const { scrollY } = useViewportScroll()
    const windowHeight = useCurrentHeight()
    const imageRef = useRef(null)
    const [imagePos, setimagePos] = useState(0)

    useEffect(() => {
        setimagePos(imageRef.current.offsetTop)
    }, [imageRef])

    const updateImage = useCallback((entries) => {
        for (let entry of entries) {
            setimagePos(entry.target.offsetTop)
        }
    }, [])

    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            updateImage(entries)
        })
        imageRef && resizeObserver.observe(imageRef.current)
        return () => resizeObserver.disconnect()
    }, [imageRef])

    const imageY = useSpring(
        useTransform(scrollY, [imagePos - windowHeight, imagePos], [100, 0]),
        { damping: 22, mass: 0.5, stiffness: 90 }
    )

    return (
        <StyledCol ref={imageRef} xs={mobileWidth} md={width} {...rest}>
            <StyledFrame
                $bgImage={bgImage}
                as={motion.div}
                style={{
                    x: imageY,
                }}
            />
        </StyledCol>
    )
}

export default BlockImage
